import React, { useState, useCallback, useMemo, useRef } from "react";
import { Locate } from "lucide-react";
import type { TimelineItem } from "@/models/legislation/Timeline";
import type { InboundChange } from "@/models/legislation/metadata/ConsolidationMetadata";
import type { ConsolidationProgressDisplay } from "@/lib/consolidationProgressDisplay";
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import TimelineStrip, { type TimelineStripHandle } from "./TimelineStrip";
import TimelineLegend from "./TimelineLegend";
import DateNavigator, { type ZoomReason } from "./DateNavigator";
import ZoomedBranchView from "./ZoomedBranchView";
import { flattenTimeline } from "@/lib/diffViewHtml";
import {
  isBekendtgoerelseTimeline,
  type TimelineSelection,
} from "./timelineNodeAppearance";

type CompactTimelineProps = {
  root: TimelineItem;
  currentAccessionNumber: string;
  currentFlatTimelineIndex?: number | null;
  timelineSelection?: TimelineSelection | null;
  datasource: string;
  onNavigate: (
    accessionNumber: string,
    event?: React.MouseEvent,
    flatTimelineIndex?: number
  ) => void;
  compareMode?: boolean;
  compareTargetAccessionNumber?: string | null;
  onCompareSelect?: (item: TimelineItem) => void;
  /**
   * When provided, only these accessions are selectable compare targets (in
   * either direction). When null/undefined, falls back to "earlier only".
   */
  eligibleCompareAccessions?: Set<string> | null;
  inboundChanges?: InboundChange[];
  /** Inbound changes per LBK window anchor, for plotting diamonds across all windows. */
  inboundByAnchor?: ReadonlyMap<string, ReadonlyArray<InboundChange>>;
  anchorConsolidationProgress?: ConsolidationProgressDisplay | null;
  anchorLawAccession?: string;
  showConsolidationDiamonds?: boolean;
  onOpenConsolidation?: (
    changelawAccession: string,
    options: {
      asOf: string | null;
      flatTimelineIndex: number;
    },
    event?: React.MouseEvent
  ) => void;
  /** Accessions to wash with the selection tint (prioritization scope preview). */
  highlightedAccessions?: ReadonlySet<string> | null;
};

type ZoomedState = {
  date: Date;
  candidates: TimelineItem[];
  reason: ZoomReason;
} | null;

const CompactTimeline: React.FC<CompactTimelineProps> = ({
  root,
  currentAccessionNumber,
  currentFlatTimelineIndex = null,
  timelineSelection = null,
  datasource: _datasource,
  onNavigate,
  compareMode = false,
  compareTargetAccessionNumber,
  onCompareSelect,
  eligibleCompareAccessions = null,
  inboundChanges,
  inboundByAnchor,
  anchorConsolidationProgress = null,
  anchorLawAccession,
  showConsolidationDiamonds = false,
  onOpenConsolidation,
  highlightedAccessions = null,
}) => {
  const [zoomedState, setZoomedState] = useState<ZoomedState>(null);
  const [isActiveOffScreen, setIsActiveOffScreen] = useState(false);
  const stripRef = useRef<TimelineStripHandle>(null);

  const flatItems = useMemo(() => flattenTimeline([root]), [root]);

  const isBeforeCurrent = useCallback(
    (item: TimelineItem): boolean => {
      const currentIdx = flatItems.findIndex(
        (n) => n.accessionNumber === currentAccessionNumber
      );
      const itemIdx = flatItems.findIndex(
        (n) => n.accessionNumber === item.accessionNumber
      );
      return currentIdx !== -1 && itemIdx !== -1 && itemIdx < currentIdx;
    },
    [flatItems, currentAccessionNumber]
  );

  const canSelectAsCompareTarget = useCallback(
    (item: TimelineItem): boolean => {
      const accession = item.accessionNumber;
      if (!accession) return false;
      if (eligibleCompareAccessions) {
        return (
          accession !== currentAccessionNumber &&
          eligibleCompareAccessions.has(accession)
        );
      }
      return isBeforeCurrent(item);
    },
    [eligibleCompareAccessions, currentAccessionNumber, isBeforeCurrent]
  );

  const handleNodeClick = useCallback(
    (
      item: TimelineItem,
      event?: React.MouseEvent,
      flatTimelineIndex?: number
    ) => {
      if (!item.accessionNumber) return;
      if (compareMode && onCompareSelect) {
        if (canSelectAsCompareTarget(item)) {
          onCompareSelect(item);
        }
      } else {
        onNavigate(item.accessionNumber, event, flatTimelineIndex);
      }
    },
    [compareMode, canSelectAsCompareTarget, onNavigate, onCompareSelect]
  );

  const handleZoomOut = useCallback(
    (date: Date, candidates: TimelineItem[], reason: ZoomReason) => {
      setZoomedState({ date, candidates, reason });
    },
    []
  );

  const handleZoomedSelect = useCallback(
    (accessionNumber: string) => {
      setZoomedState(null);
      if (compareMode && onCompareSelect) {
        const item =
          flatItems.find((n) => n.accessionNumber === accessionNumber) ?? null;
        if (item && canSelectAsCompareTarget(item)) {
          onCompareSelect(item);
        }
      } else {
        onNavigate(accessionNumber);
      }
    },
    [
      compareMode,
      flatItems,
      canSelectAsCompareTarget,
      onNavigate,
      onCompareSelect,
    ]
  );

  const handleZoomedClose = useCallback(() => {
    setZoomedState(null);
  }, []);

  return (
    <>
      <div className="relative flex items-center gap-4 min-w-0 min-h-24 pr-8">
        <DateNavigator
          root={root}
          currentAccessionNumber={currentAccessionNumber}
          onNavigate={onNavigate}
          onZoomOut={handleZoomOut}
        />

        <div className="flex-1 min-w-0">
          <TimelineStrip
            ref={stripRef}
            root={root}
            currentAccessionNumber={currentAccessionNumber}
            currentFlatTimelineIndex={currentFlatTimelineIndex}
            timelineSelection={timelineSelection}
            onNodeClick={handleNodeClick}
            onActiveOffScreenChange={setIsActiveOffScreen}
            compareMode={compareMode}
            compareTargetAccessionNumber={compareTargetAccessionNumber}
            eligibleCompareAccessions={eligibleCompareAccessions}
            inboundChanges={inboundChanges}
            inboundByAnchor={inboundByAnchor}
            anchorConsolidationProgress={anchorConsolidationProgress}
            anchorLawAccession={anchorLawAccession}
            showConsolidationDiamonds={showConsolidationDiamonds}
            onOpenConsolidation={onOpenConsolidation}
            highlightedAccessions={highlightedAccessions}
          />
        </div>

        {isActiveOffScreen ? (
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="absolute right-0 top-1/2 z-10 size-7 -translate-y-1/2 bg-background shadow-sm"
                  onClick={() => stripRef.current?.centerOnActive()}
                  aria-label="Centrer aktiv version på tidslinjen"
                >
                  <Locate className="size-3.5" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Gå til aktiv</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}

        <TimelineLegend
          anchorConsolidationProgress={anchorConsolidationProgress}
          isBekTimeline={isBekendtgoerelseTimeline(root)}
        />
      </div>

      {zoomedState && (
        <ZoomedBranchView
          root={root}
          currentAccessionNumber={currentAccessionNumber}
          targetDate={zoomedState.date}
          candidates={zoomedState.candidates}
          reason={zoomedState.reason}
          onSelect={handleZoomedSelect}
          onClose={handleZoomedClose}
          compareMode={compareMode}
          eligibleCompareAccessions={eligibleCompareAccessions}
        />
      )}
    </>
  );
};

export default CompactTimeline;
